import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EmployeeLookup,
  type Employee,
  useEmployeeLookup,
} from '@amzn/stencil-react-components/employee-lookup';

import { peopleService, type PeopleSuggestion } from '../data';

/**
 * Minimum characters typed before the SuggestPeople endpoint is
 * queried. Matches the legacy desktop picker so the two feel
 * identical to auditors switching between shells.
 */
const MIN_QUERY_CHARS = 2;

/**
 * Debounce applied by Stencil's `useEmployeeLookup` before the query
 * callback fires. Same 300ms the desktop picker uses.
 */
const DEBOUNCE_MS = 300;

/**
 * Max suggestions requested per query. Server enforces its own cap;
 * 10 is what shows on screen without scrolling on a mobile listbox.
 */
const SUGGEST_LIMIT = 10;

/**
 * Cap on the dropdown list height. Combined with
 * `listPositioningStrategy="fixed"` below, this prevents the results
 * list from expanding the modal's scroll container — the list gets
 * its own bounded overflow instead of pushing the modal content
 * down.
 */
const LIST_MAX_HEIGHT_PX = 240;

/**
 * Stable empty results array. Used to suppress the underlying
 * Stencil widget's stale-results flash for one render after a
 * commit with `clearOnSelect` — see `handleSelectionChange`.
 * Module-level so the reference is identity-stable across renders
 * (no fresh `[]` literal in JSX props).
 */
// `readonly` in place of `Object.freeze` per repo lint rules —
// consumers can't mutate the array through the exported type.
const EMPTY_RESULTS: readonly Employee[] = [];

export interface EmployeePickerProps {
  /**
   * Program scope for the suggest call. Take from
   * `useCurrentUser().programId` — the same programId the rest of
   * the app queries user data with.
   */
  readonly programId: string;
  /**
   * Currently committed login (canonical, lowercase), or `null` when
   * nothing is selected. Passing `null` externally resets the
   * picker (e.g. after a filter clear-all or a modal that reopens).
   */
  readonly value: string | null;
  /**
   * Called when the user selects a suggestion. Always yields a
   * canonical login; `fullName` is best-effort display data. Free
   * text never becomes a committed value.
   */
  readonly onSelect: (login: string, fullName?: string) => void;
  /**
   * Called when the field is cleared via the built-in clear
   * button. Optional — some consumers keep the value even when the
   * search field is empty (e.g. a filter dropdown).
   */
  readonly onClear?: () => void;
  /**
   * When true, the picker resets its search query AND its committed
   * selection immediately after each successful commit — the auditor
   * can keep typing and add another person without a manual clear.
   * Focus stays in the input because the reset is a state change,
   * not a remount.
   *
   * Default false: single-value form fields and filter dropdowns
   * want to KEEP the selection visible after commit.
   */
  readonly clearOnSelect?: boolean;
  /**
   * When provided, the picker offers a free-text fallback row
   * whenever the server returns zero suggestions AND the validator
   * accepts the typed query. The row is committed like any real
   * suggestion — the emitted login is exactly what was typed
   * (trimmed), and `fullName` mirrors it. Consumers pass their own
   * validator (e.g. `isValidAlias` for assignee flows) so the
   * synthesized row only appears when the typed text will pass
   * downstream submit-time validation, avoiding a rug-pull between
   * "I can pick this" and "this is rejected".
   *
   * Motivation: in beta / dev the SuggestPeople endpoint may return
   * an empty result for aliases that don't exist in the indexed set
   * yet. Without this fallback the auditor is stuck — nothing to
   * pick means nothing to commit. Leaving the prop undefined
   * preserves the strict "canonical login from a returned
   * suggestion" contract for consumers (filter dropdowns, etc.)
   * that don't want it.
   */
  readonly freeTextValidator?: (query: string) => boolean;
  /**
   * Called on every keystroke in the search field with the current
   * (untrimmed) query string. Optional — most consumers rely on
   * `onSelect` alone. Useful when a parent form needs to fall
   * back to the typed text on Submit if the auditor hasn't picked
   * a suggestion, e.g. a reassign modal where "type a name and
   * click Confirm" should work without an intermediate click on a
   * dropdown row.
   */
  readonly onTextChange?: (query: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  readonly dataTestId?: string;
}

/**
 * v2 employee autocomplete.
 *
 * Wraps Stencil's `EmployeeLookup` around Orbit's SuggestPeople
 * endpoint. The committed value is ALWAYS a canonical login taken
 * from a returned suggestion — free text is never surfaced as a
 * selectable option. Mirrors the desktop `EmployeePicker`'s
 * canonical-login contract so the two shells stay behaviourally
 * identical on the assignee flows.
 *
 *   <EmployeePicker
 *     programId={programId}
 *     value={currentLogin}
 *     onSelect={(login) => setLogin(login)}
 *     onClear={() => setLogin('')}
 *     placeholder={t('assigneesInputPlaceholder')}
 *   />
 *
 * Composition rationale:
 *
 * - Stencil's `useEmployeeLookup` owns the debounce + Promise
 *   state (loading/error/results) — we hand it a `Promise<Employee[]>`
 *   producer and pass its return through to `EmployeeLookup`.
 * - Adapter layer converts Orbit's `PeopleSuggestion` into Stencil's
 *   `Employee` shape (`login` → `username`, `fullName` → `fullName`).
 *   Kept minimal so both types stay decoupled — future Orbit fields
 *   don't require Stencil's shape to grow.
 * - Local state holds the last-committed `Employee` so
 *   `EmployeeLookup.value` (which takes `Employee | null`, not
 *   a plain string) has something to render. The parent's `value`
 *   prop is still the source of truth for logic — a `null` value
 *   from the parent clears the local state on the next render.
 */
export function EmployeePicker({
  programId,
  value,
  onSelect,
  onClear,
  clearOnSelect = false,
  freeTextValidator,
  onTextChange,
  placeholder,
  disabled = false,
  ariaLabel,
  dataTestId,
}: EmployeePickerProps): JSX.Element {
  // Latch the last-selected suggestion so the picker can render its
  // committed state. Tracks by login — when the parent passes a
  // different login than we last committed (external reset, or the
  // parent explicitly setting a different value), we clear our
  // local selection so the picker shows the empty state and the
  // fresh value string.
  const [selected, setSelected] = useState<Employee | null>(
    value !== null ? { username: value, fullName: value } : null,
  );
  const lastCommittedLoginRef = useRef<string | null>(value);
  // Controlled search text. Owning this state (instead of letting
  // Stencil's `useEmployeeLookup` be the only source of truth) lets
  // us reset the input on commit for `clearOnSelect` consumers.
  // `handleTextChange` mirrors changes into the hook so its
  // debounce trigger + results stay coherent with what the user
  // sees in the field.
  const [query, setQuery] = useState('');
  // Masks the hook's `results` for one render after a
  // `clearOnSelect` reset. Reason: setting the hook's query to ''
  // does not synchronously clear its `results` state — the debounced
  // empty-query fetch has to settle first (~300ms). Without this
  // mask, the dropdown briefly shows the just-committed suggestion
  // row on top of a now-empty input, which reads as a bug.
  const [suppressResults, setSuppressResults] = useState(false);

  // Sync local selection to the parent's `value` prop when it
  // TRANSITIONS. Firing this in an effect (rather than in the render
  // body) is deliberate: an internal commit leaves `value` untouched
  // (parents that opt into `clearOnSelect` pass `value={null}`
  // permanently and don't drive the committed state themselves).
  // The effect only re-runs when `value` actually changes, so a
  // commit doesn't accidentally cancel itself. Controlled consumers
  // — e.g. a filter dropdown whose parent owns the login — still
  // get their reset / hydrate semantics because their `value` prop
  // is the one that changes.
  // Direct setState inside this effect is intentional and safe: the
  // effect only runs on the `value` PROP TRANSITION, not on our own
  // state changes, so it can't self-trigger a loop. Render-time
  // adjustment (the pattern the rule prefers) would mistake an
  // internal commit for a "value went back to null" transition and
  // clobber the local selection. See docblock above.
  /* eslint-disable @eslint-react/hooks-extra/no-direct-set-state-in-use-effect */
  useEffect(() => {
    if (value === null) {
      lastCommittedLoginRef.current = null;
      setSelected(null);
      return;
    }
    if (value !== lastCommittedLoginRef.current) {
      // Parent set a login we didn't commit (URL hydration, external
      // form reset). Render the raw login as a minimal Employee so
      // the field shows SOMETHING readable; the user can clear and
      // re-pick to attach a display name.
      lastCommittedLoginRef.current = value;
      setSelected({ username: value, fullName: value });
    }
  }, [value]);
  /* eslint-enable @eslint-react/hooks-extra/no-direct-set-state-in-use-effect */

  const handleQuery = useCallback(
    async (query: string): Promise<Employee[]> => {
      // Length gate — server would return quickly on 1-char queries
      // but the results are noisy. Match desktop's floor.
      const trimmed = query.trim();
      if (!programId || trimmed.length < MIN_QUERY_CHARS) return [];
      const response = await peopleService.suggest({
        programId,
        query: trimmed,
        limit: SUGGEST_LIMIT,
      });
      const suggestions = response.suggestions.map(mapSuggestionToEmployee);
      // Free-text fallback: when the server returned nothing and the
      // consumer opted into fallback via a validator that accepts
      // the typed query, synthesize a single row so the auditor can
      // commit the typed alias. See the `freeTextValidator` docblock
      // on the props interface for why this exists.
      if (suggestions.length === 0 && freeTextValidator?.(trimmed) === true) {
        return [{ username: trimmed, fullName: trimmed }];
      }
      return suggestions;
    },
    [programId, freeTextValidator],
  );

  const { employeeLookupProps } = useEmployeeLookup({
    debounceTimeout: DEBOUNCE_MS,
    onQueryChange: handleQuery,
  });

  const hookOnQueryChange = employeeLookupProps.onQueryChange;

  const handleTextChange = useCallback(
    (next: string): void => {
      // Two-way mirror: our local state drives what Stencil renders
      // in the field, and the hook needs to see the same value so
      // its internal debounce timer + results state stay coherent.
      setQuery(next);
      // Fresh input invalidates a previous `clearOnSelect` mask —
      // stop suppressing results.
      setSuppressResults(false);
      hookOnQueryChange(next);
      // Expose the raw query to consumers that need it (see
      // `onTextChange` docblock — reassign-style forms fall back
      // to the typed text on Submit).
      onTextChange?.(next);
    },
    [hookOnQueryChange, onTextChange],
  );

  const handleSelectionChange = useCallback(
    (next: Employee | Employee[] | undefined): void => {
      // Single-select only — the multi variant of Stencil's picker
      // hands back an array but this component doesn't opt into
      // `isMulti`, so we defensively unwrap.
      const picked = Array.isArray(next) ? next.at(0) : next;
      if (!picked?.username) {
        // Cleared via the built-in clear button (or a keyboard
        // interaction that reset state). Reset the entire local
        // world — committed selection, query text, hook query, and
        // any typed-text mirror consumers subscribed to. Otherwise
        // a lingering `typedText` in a parent form could sneak into
        // a Submit-time fallback after the auditor explicitly
        // cleared, which reads as "the field was empty but the
        // reassign still happened".
        lastCommittedLoginRef.current = null;
        setSelected(null);
        setQuery('');
        hookOnQueryChange('');
        onTextChange?.('');
        onClear?.();
        return;
      }
      lastCommittedLoginRef.current = picked.username;
      onSelect(picked.username, picked.fullName);
      if (clearOnSelect) {
        // Full reset: the input, the committed selection, and the
        // hook's debounce trigger. Sync all three so the auditor
        // can immediately start typing the next name and see fresh
        // results (not a lingering "alice" row from the commit
        // they just made).
        setQuery('');
        hookOnQueryChange('');
        setSuppressResults(true);
        setSelected(null);
        lastCommittedLoginRef.current = null;
        return;
      }
      setSelected(picked);
    },
    [clearOnSelect, hookOnQueryChange, onClear, onSelect, onTextChange],
  );

  // dataTestId is spread through the standard SearchField
  // pass-through — conditionally-spread so an undefined value
  // doesn't render an empty `data-testid=""` attribute.
  const testIdProps = useMemo(
    () => (dataTestId !== undefined ? { 'data-testid': dataTestId } : {}),
    [dataTestId],
  );

  // Prefer the module-level stable `EMPTY_RESULTS` over an inline
  // `[]` so the reference doesn't churn across renders when the
  // suppression is active.
  const resolvedResults = suppressResults ? EMPTY_RESULTS : employeeLookupProps.results;

  return (
    <EmployeeLookup
      {...employeeLookupProps}
      query={query}
      onQueryChange={handleTextChange}
      results={resolvedResults as Employee[]}
      value={selected}
      onSelectionChange={handleSelectionChange}
      placeholder={placeholder ?? DEFAULT_PLACEHOLDER}
      disabled={disabled}
      aria-label={ariaLabel ?? DEFAULT_ARIA_LABEL}
      // Position the dropdown against the viewport (position: fixed)
      // instead of Popper's default position: absolute. `absolute`
      // anchors the list inside the nearest positioned ancestor — for
      // a bottom-sheet modal that ancestor is the modal itself, which
      // has `overflow: auto`, so the list ends up extending the
      // modal's scroll instead of overlaying it. `fixed` escapes
      // that container: the list floats above the modal at the
      // viewport level, so the user sees suggestions without
      // scrolling the modal body.
      listPositioningStrategy="fixed"
      // Cap the list height so 10 rows don't fill the screen. The
      // list gets its own internal scroll for the rare case where
      // the auditor wants to browse beyond the visible window.
      listMaxHeight={LIST_MAX_HEIGHT_PX}
      {...testIdProps}
    />
  );
}

/**
 * Adapter: Orbit `PeopleSuggestion` → Stencil `Employee`. Kept as a
 * top-level pure function so the mapping stays testable in isolation
 * without spinning up the picker.
 */
function mapSuggestionToEmployee(suggestion: PeopleSuggestion): Employee {
  return {
    username: suggestion.login,
    // Stencil requires `fullName` (non-optional). Fall back to the
    // login when the server didn't return a display name — the
    // dropdown row shows something readable either way.
    fullName: suggestion.fullName ?? suggestion.login,
  };
}

const DEFAULT_PLACEHOLDER = 'Search by alias or name';
const DEFAULT_ARIA_LABEL = 'Employee search';
